import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useOrganisme } from '../context/OrganismeContext';
import { 
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

// ── Sélecteur d'heure par quart d'heure ──────────────────────────────────────
const HOURS   = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
const MINUTES = ['00', '15', '30', '45'];

function TimeSelect({ value, onChange, placeholder = 'Heure' }) {
  const parts = value ? value.split(':') : [];
  const hh = parts[0] || '';
  const mm = parts[1] || '00';

  const setHour = (newH) => {
    if (!newH) { onChange(''); return; }
    onChange(`${newH}:${mm}`);
  };
  const setMinute = (newM) => {
    if (!hh) return;
    onChange(`${hh}:${newM}`);
  };

  return (
    <div className="flex items-center gap-1">
      <select
        value={hh}
        onChange={e => setHour(e.target.value)}
        className="w-20 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white"
      >
        <option value="">--</option>
        {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
      </select>
      <span className="text-gray-400 font-semibold select-none">:</span>
      <select
        value={mm}
        onChange={e => setMinute(e.target.value)}
        disabled={!hh}
        className="w-16 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500 bg-white disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {MINUTES.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  );
}

const STATUS_CONFIG = {
  brouillon: { label: 'Brouillon',  badge: 'bg-gray-100 text-gray-700 border-gray-200' },
  publie:    { label: 'Publié',     badge: 'bg-green-100 text-green-700 border-green-200' },
  annule:    { label: 'Annulé',     badge: 'bg-red-100 text-red-700 border-red-200' },
  termine:   { label: 'Terminé',    badge: 'bg-blue-100 text-blue-700 border-blue-200' },
};


export default function CreateEvent() {
  const { user } = useAuth();
  const { organismeActif, organismes } = useOrganisme();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  // Mode référent : accès via /events/:id/edit (hors panel admin)
  const isReferentMode = !location.pathname.startsWith('/admin');

  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [, setInitialStatus] = useState('brouillon');
  const [coverImage, setCoverImage] = useState(null);   // { url, originalName } chargé depuis l'API
  const [coverImageFile, setCoverImageFile] = useState(null);   // File sélectionné localement
  const [coverImagePreview, setCoverImagePreview] = useState(null); // URL blob preview
  const [coverImageLoading, setCoverImageLoading] = useState(false);
  const userRoles = user?.roles || [user?.role];
  const isAdmin = userRoles.includes('admin') || userRoles.includes('super_admin');
  const [users, setUsers] = useState([]);
  const [referentSearch, setReferentSearch] = useState('');
  const [showReferentDropdown, setShowReferentDropdown] = useState(false);
  // Liste de tous les organismes — chargée depuis l'API en mode référent
  const [allOrganismes, setAllOrganismes] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'brouillon',
    organismesConcernes: [organismeActif].filter(Boolean),
    organismesOrganisateurs: [organismeActif].filter(Boolean),
    referents: [],
    dates: [{
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      description: '',
      location: {
        name: '',
        address: '',
        city: '',
        postalCode: '',
        additionalInfo: ''
      }
    }]
  });

  /** Clé stable pour recharger la liste quand les organismes cochés changent */
  const referentOrgKey = useMemo(
    () =>
      [...new Set([...formData.organismesConcernes, ...formData.organismesOrganisateurs])]
        .filter(Boolean)
        .sort()
        .join(','),
    [formData.organismesConcernes, formData.organismesOrganisateurs]
  );

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const union = referentOrgKey ? referentOrgKey.split(',') : [];
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/events/available-referents`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: union.length ? { organismes: union.join(',') } : {},
        }
      );
      setUsers(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des référents disponibles:', error);
    }
  }, [referentOrgKey]);

  // En mode référent, charger la liste de tous les organismes depuis l'API
  const fetchOrganismesList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/events/organismes-list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAllOrganismes(response.data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des organismes:', error);
    }
  };

  useEffect(() => {
    if (isReferentMode) fetchOrganismesList();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isReferentMode) fetchUsers();
  }, [isReferentMode, fetchUsers]);

  useEffect(() => {
    // Vérifier si on est en mode édition
    if (id) {
      setIsEditing(true);
      fetchEventData();
    } else {
      setInitialLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchEventData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/events/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const event = response.data;
      
      // Préremplir le formulaire avec les données de l'événement
      setInitialStatus(event.status || 'brouillon');
      if (event.coverImage?.s3Key) {
        setCoverImage({
          url:          event.coverImage.url || null,
          originalName: event.coverImage.originalName || null,
        });
      }
      const concernes = event.organismesConcernes?.length
        ? event.organismesConcernes
        : (event.organismes || []);
      const organisateurs = event.organismesOrganisateurs?.length
        ? event.organismesOrganisateurs
        : concernes;
      setFormData({
        title: event.title || '',
        description: event.description || '',
        status: event.status || 'brouillon',
        organismesConcernes: concernes.length ? concernes : [organismeActif].filter(Boolean),
        organismesOrganisateurs: organisateurs.length ? organisateurs : [organismeActif].filter(Boolean),
        referents: event.referents?.map(ref => ref._id) || [],
        dates: event.dates?.length > 0 ? event.dates.map(date => ({
          startDate: date.startDate ? new Date(date.startDate).toISOString().split('T')[0] : '',
          endDate: date.endDate ? new Date(date.endDate).toISOString().split('T')[0] : '',
          startTime: date.startTime || '',
          endTime: date.endTime || '',
          description: date.description || '',
          location: typeof date.location === 'string' ? {
            name: '',
            address: date.location,
            city: '',
            postalCode: '',
            additionalInfo: ''
          } : {
            name: date.location?.name || '',
            address: date.location?.address || '',
            city: date.location?.city || '',
            postalCode: date.location?.postalCode || '',
            additionalInfo: date.location?.additionalInfo || ''
          }
        })) : [{
          startDate: '',
          endDate: '',
          startTime: '',
          endTime: '',
          description: '',
          location: {
            name: '',
            address: '',
            city: '',
            postalCode: '',
            additionalInfo: ''
          }
        }]
      });
    } catch (error) {
      console.error('Erreur lors du chargement de l\'événement:', error);
      window.alert('Erreur lors du chargement de l\'événement');
    } finally {
      setInitialLoading(false);
    }
  };

  // Gérer les changements des champs simples
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleOrganismeFieldChange = (field, organisme, checked) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
        ? [...prev[field], organisme]
        : prev[field].filter(org => org !== organisme)
    }));
  };


  // Gérer les changements des dates (date de fin = date de début par défaut à la saisie du début)
  const handleDateChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.map((date, i) => {
        if (i !== index) return date;
        if (field === 'startDate') {
          const next = { ...date, startDate: value };
          if (value) {
            next.endDate = value;
          }
          return next;
        }
        return { ...date, [field]: value };
      }),
    }));
  };

  // Gérer les changements des lieux
  const handleLocationChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      dates: prev.dates.map((date, i) => 
        i === index ? { 
          ...date, 
          location: { ...date.location, [field]: value }
        } : date
      )
    }));
  };

  // Ajouter une nouvelle date — lieu prérempli comme la première date
  const addDate = () => {
    setFormData((prev) => {
      const first = prev.dates[0]?.location;
      const locationCopy = first
        ? {
            name: first.name || '',
            address: first.address || '',
            city: first.city || '',
            postalCode: first.postalCode || '',
            additionalInfo: first.additionalInfo || '',
          }
        : {
            name: '',
            address: '',
            city: '',
            postalCode: '',
            additionalInfo: '',
          };
      return {
        ...prev,
        dates: [
          ...prev.dates,
          {
            startDate: '',
            endDate: '',
            startTime: '',
            endTime: '',
            description: '',
            location: locationCopy,
          },
        ],
      };
    });
  };

  // Supprimer une date
  const removeDate = (index) => {
    if (formData.dates.length > 1) {
      setFormData(prev => ({
        ...prev,
        dates: prev.dates.filter((_, i) => i !== index)
      }));
    }
  };

  // Liste déjà filtrée côté API (adhérents actifs + pilotes module événements pour les organismes sélectionnés)
  const getFilteredReferents = () => {
    const available = users;
    if (!referentSearch.trim()) return available.slice(0, 10); // Limiter à 10 par défaut
    
    return available.filter(user => {
      const searchTerm = referentSearch.toLowerCase();
      return (
        user.nom.toLowerCase().includes(searchTerm) ||
        user.prenom.toLowerCase().includes(searchTerm) ||
        user.email.toLowerCase().includes(searchTerm) ||
        `${user.nom} ${user.prenom}`.toLowerCase().includes(searchTerm)
      );
    }).slice(0, 10); // Limiter à 10 résultats
  };

  // Ajouter un référent
  const addReferent = (userId) => {
    if (!formData.referents.includes(userId)) {
      setFormData(prev => ({
        ...prev,
        referents: [...prev.referents, userId]
      }));
    }
    setReferentSearch('');
    setShowReferentDropdown(false);
  };

  // Supprimer un référent
  const removeReferent = (userId) => {
    setFormData(prev => ({
      ...prev,
      referents: prev.referents.filter(id => id !== userId)
    }));
  };

  // Obtenir les référents sélectionnés
  const getSelectedReferents = () => {
    return users.filter(user => formData.referents.includes(user._id));
  };

  // --- Cover image ---
  const handleCoverImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCoverImageFile(file);
    setCoverImagePreview(URL.createObjectURL(file));
  };

  const handleCoverImageDelete = async () => {
    if (!id) return;
    if (!window.confirm('Supprimer l\'image de couverture ?')) return;
    try {
      setCoverImageLoading(true);
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/events/${id}/cover-image`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCoverImage(null);
      setCoverImageFile(null);
      if (coverImagePreview) URL.revokeObjectURL(coverImagePreview);
      setCoverImagePreview(null);
    } catch (err) {
      window.alert('Erreur lors de la suppression de l\'image');
    } finally {
      setCoverImageLoading(false);
    }
  };

  const descriptionTextPlain = (html) =>
    (html || '').replace(/<[^>]+>/g, '').replace(/&nbsp;/gi, ' ').trim();

  // Validation commune
  const validateForm = () => {
    if (!formData.title.trim() || !descriptionTextPlain(formData.description)) {
      window.alert('Le titre et la description sont obligatoires');
      return false;
    }
    if (formData.organismesConcernes.length === 0 || formData.organismesOrganisateurs.length === 0) {
      window.alert('Veuillez sélectionner au moins un organisme concerné et au moins un organisme organisateur');
      return false;
    }
    if (!isReferentMode && formData.referents.length === 0) {
      window.alert('Veuillez sélectionner au moins un référent');
      return false;
    }
    if (formData.dates.some(date => !date.startDate || !date.endDate || !date.location.name)) {
      window.alert('Veuillez renseigner toutes les dates de début, de fin et les lieux');
      return false;
    }
    if (formData.dates.some(date => !date.endTime)) {
      window.alert('Veuillez renseigner l\'heure de fin pour chaque date');
      return false;
    }
    return true;
  };

  // Sauvegarder l'événement
  const doSave = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      let savedEventId = id;

      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/events/${id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        const res = await axios.post(
          `${process.env.REACT_APP_API_URL}/events`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        savedEventId = res.data._id || res.data.event?._id;
      }

      // Upload de la cover image si un fichier a été sélectionné
      if (coverImageFile && savedEventId) {
        try {
          const imgData = new FormData();
          imgData.append('coverImage', coverImageFile);
          await axios.post(
            `${process.env.REACT_APP_API_URL}/events/${savedEventId}/cover-image`,
            imgData,
            { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
          );
        } catch (imgErr) {
          console.error('Erreur upload cover image:', imgErr);
          // Ne bloque pas la navigation, l'événement est sauvegardé
        }
      }

      if (isReferentMode) {
        navigate(`/events/${savedEventId}`);
      } else {
        navigate('/admin/events');
      }
    } catch (error) {
      console.error(`Erreur lors de ${isEditing ? 'la modification' : 'la création'}:`, error);
      window.alert(`Erreur lors de ${isEditing ? 'la modification' : 'la création'} de l'événement`);
    } finally {
      setLoading(false);
    }
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    await doSave();
  };

  if (initialLoading) {
    return (
      <div className="h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement de l'événement...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {isEditing ? 'Modifier un événement' : 'Créer un événement'}
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                {isEditing ? "Modifiez les informations de l'événement" : 'Organisez un nouvel événement pour vos adhérents'}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => isReferentMode ? navigate(`/events/${id}`) : navigate('/admin/events')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-8 py-6">
          <form id="event-form" onSubmit={handleSubmit} className="mt-6 space-y-8">

            {/* Statut de l'événement — edit mode uniquement */}
            {isEditing && (isAdmin || isReferentMode) && (
              <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
                <div className="mb-4">
                  <h3 className="text-lg font-medium leading-6 text-gray-900">Statut de l'événement</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Contrôlez la visibilité de l'événement auprès des adhérents
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  {/* Badge statut actuel */}
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${STATUS_CONFIG[formData.status]?.badge || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                    {STATUS_CONFIG[formData.status]?.label || formData.status}
                  </span>
                  {/* Select nouveau statut */}
                  {formData.status !== 'termine' ? (
                    <div className="flex items-center gap-2">
                      <label htmlFor="event-status" className="text-sm text-gray-600">Changer en :</label>
                      <select
                        id="event-status"
                        value={formData.status}
                        onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="brouillon">Brouillon</option>
                        <option value="publie">Publié</option>
                        <option value="annule">Annulé</option>
                      </select>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      Le statut "Terminé" est géré automatiquement et ne peut pas être modifié manuellement.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Informations générales */}
            <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Informations générales
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Titre, description, organismes concernés et organisateurs
            </p>
          </div>
          <div className="space-y-6">
            {/* Titre */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                Titre de l'événement *
              </label>
              <input
                type="text"
                name="title"
                id="title"
                required
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <Editor
                apiKey="yz5jdxbkp4i9v4omefqdsmbc6pxaqmyor9p6sh5cfi2f3gz5"
                value={formData.description}
                onEditorChange={(content) =>
                  setFormData((prev) => ({ ...prev, description: content }))
                }
                init={{
                  height: 400,
                  menubar: false,
                  plugins: ['lists', 'link', 'code'],
                  toolbar:
                    'undo redo | bold italic underline | alignleft aligncenter alignright | bullist numlist | link | code',
                  content_style:
                    'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; }',
                  language: 'fr_FR',
                }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Organismes concernés *
              </label>
              <p className="mt-0.5 text-xs text-gray-500">Adhérents de ces organismes voient l&apos;événement une fois publié.</p>
              <div className="mt-2 space-y-2">
                {(isReferentMode ? allOrganismes : organismes).map(organisme => (
                  <div key={`c-${organisme}`} className="flex items-center">
                    <input
                      id={`organisme-concerne-${organisme}`}
                      type="checkbox"
                      checked={formData.organismesConcernes.includes(organisme)}
                      onChange={(e) => handleOrganismeFieldChange('organismesConcernes', organisme, e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`organisme-concerne-${organisme}`} className="ml-2 text-sm text-gray-700">
                      {organisme}
                    </label>
                  </div>
                ))}
                {isReferentMode && allOrganismes.length === 0 && (
                  <p className="text-sm text-gray-400 italic">Chargement des organismes…</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                Organismes organisateurs *
              </label>
              <p className="mt-0.5 text-xs text-gray-500">Pilotes de ces organismes reçoivent les notifications de volontariat (en plus des référents).</p>
              <div className="mt-2 space-y-2">
                {(isReferentMode ? allOrganismes : organismes).map(organisme => (
                  <div key={`o-${organisme}`} className="flex items-center">
                    <input
                      id={`organisme-orga-${organisme}`}
                      type="checkbox"
                      checked={formData.organismesOrganisateurs.includes(organisme)}
                      onChange={(e) => handleOrganismeFieldChange('organismesOrganisateurs', organisme, e.target.checked)}
                      className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`organisme-orga-${organisme}`} className="ml-2 text-sm text-gray-700">
                      {organisme}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Image de couverture */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="mb-4">
            <h3 className="text-lg font-medium leading-6 text-gray-900">Image de couverture</h3>
            <p className="mt-1 text-sm text-gray-500">
              Ajoutez une image illustrant l'événement (JPEG, PNG ou WebP, max 5 Mo). L'image sera enregistrée avec le formulaire.
            </p>
          </div>

          {/* Aperçu : image existante (edit) ou nouveau fichier sélectionné */}
          {(coverImagePreview || coverImage?.url) && (
            <div className="mb-4 relative inline-block">
              <img
                src={coverImagePreview || coverImage.url}
                alt="Couverture"
                className="max-h-56 rounded-lg object-cover border border-gray-200 shadow-sm"
              />
              {isEditing && coverImage?.url && !coverImagePreview && (
                <button
                  type="button"
                  onClick={handleCoverImageDelete}
                  disabled={coverImageLoading}
                  className="absolute top-2 right-2 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 shadow disabled:opacity-50"
                  title="Supprimer l'image"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
              <input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                className="sr-only"
                style={{left: '-100000px', right: '-100000px'}}
                onChange={handleCoverImageSelect}
              />
              {coverImage?.url ? "Remplacer l'image" : 'Choisir une image'}
            </label>

            {coverImageFile && (
              <>
                <span className="text-sm text-indigo-600 font-medium truncate max-w-xs">
                  ✓ {coverImageFile.name}
                </span>
                <span className="text-sm text-gray-400">— sera enregistrée à la sauvegarde</span>
                <button
                  type="button"
                  onClick={() => {
                    setCoverImageFile(null);
                    if (coverImagePreview) URL.revokeObjectURL(coverImagePreview);
                    setCoverImagePreview(null);
                  }}
                  className="text-sm text-red-500 hover:text-red-700"
                >
                  Annuler
                </button>
              </>
            )}
          </div>
        </div>

        {/* Référents — masqué en mode référent */}
        {!isReferentMode && <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Référents
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Sélectionnez les référents de l'événement
            </p>
          </div>
          <div className="space-y-4">
            {/* Référents sélectionnés */}
            {getSelectedReferents().length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Référents sélectionnés
                </label>
                <div className="flex flex-wrap gap-2">
                  {getSelectedReferents().map(user => (
                    <span
                      key={user._id}
                      className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800"
                    >
                      {user.nom} {user.prenom}
                      <button
                        type="button"
                        onClick={() => removeReferent(user._id)}
                        className="ml-2 inline-flex items-center justify-center w-4 h-4 rounded-full text-indigo-400 hover:bg-indigo-200 hover:text-indigo-600 focus:outline-none"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Recherche de référents */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ajouter des référents
              </label>
              <input
                type="text"
                placeholder="Rechercher par nom, prénom ou email..."
                value={referentSearch}
                onChange={(e) => {
                  setReferentSearch(e.target.value);
                  setShowReferentDropdown(true);
                }}
                onFocus={() => setShowReferentDropdown(true)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
              
              {/* Dropdown des résultats */}
              {showReferentDropdown && (
                <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                  {getFilteredReferents().length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      {users.length === 0
                        ? "Aucun utilisateur disponible pour les organismes sélectionnés"
                        : "Aucun résultat trouvé"
                      }
                    </div>
                  ) : (
                    getFilteredReferents().map(user => (
                      <button
                        key={user._id}
                        type="button"
                        onClick={() => addReferent(user._id)}
                        disabled={formData.referents.includes(user._id)}
                        className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 focus:outline-none focus:bg-gray-100 ${
                          formData.referents.includes(user._id) 
                            ? 'text-gray-400 cursor-not-allowed' 
                            : 'text-gray-900 cursor-pointer'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="font-medium">
                              {user.nom} {user.prenom}
                            </div>
                            <div className="text-gray-500">
                              {user.email}
                            </div>
                          </div>
                          {formData.referents.includes(user._id) && (
                            <span className="text-green-500 text-xs">✓ Sélectionné</span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                  
                  {referentSearch.trim() && getFilteredReferents().length === 10 && (
                    <div className="px-4 py-2 text-xs text-gray-400 border-t">
                      Affichage des 10 premiers résultats. Affinez votre recherche pour plus de précision.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Clic en dehors pour fermer le dropdown */}
            {showReferentDropdown && (
              <div 
                className="fixed inset-0 z-0" 
                onClick={() => setShowReferentDropdown(false)}
              />
            )}
          </div>
        </div>}

        {/* Dates et lieux */}
        <div className="bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
          <div className="mb-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Dates et lieux
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Ajoutez les dates et lieux de l'événement
            </p>
          </div>
          <div className="space-y-6">
            {formData.dates.map((date, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-gray-900">
                        Date {index + 1}
                      </h4>
                      {formData.dates.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeDate(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>

                    {/* Description de la date */}
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700">
                        Description de cette date (optionnel)
                      </label>
                      <input
                        type="text"
                        placeholder="ex : Journée d'ouverture, Séance de clôture…"
                        value={date.description}
                        onChange={(e) => handleDateChange(index, 'description', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      {/* Date de début */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Date de début *
                        </label>
                        <input
                          type="date"
                          required
                          value={date.startDate}
                          onChange={(e) => handleDateChange(index, 'startDate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      {/* Date de fin */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Date de fin *
                        </label>
                        <input
                          type="date"
                          required
                          value={date.endDate}
                          onChange={(e) => handleDateChange(index, 'endDate', e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      {/* Heure de début */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Heure de début
                        </label>
                        <TimeSelect
                          value={date.startTime}
                          onChange={(v) => handleDateChange(index, 'startTime', v)}
                        />
                      </div>

                      {/* Heure de fin */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Heure de fin
                        </label>
                        <TimeSelect
                          value={date.endTime}
                          onChange={(v) => handleDateChange(index, 'endTime', v)}
                        />
                      </div>
                    </div>

                    {/* Lieu */}
                    <div className="mt-4 space-y-4">
                      <h5 className="text-sm font-medium text-gray-900">Lieu</h5>
                      
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Nom du lieu *
                          </label>
                          <input
                            type="text"
                            required
                            value={date.location.name}
                            onChange={(e) => handleLocationChange(index, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Adresse
                          </label>
                          <input
                            type="text"
                            value={date.location.address}
                            onChange={(e) => handleLocationChange(index, 'address', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Ville
                          </label>
                          <input
                            type="text"
                            value={date.location.city}
                            onChange={(e) => handleLocationChange(index, 'city', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Code postal
                          </label>
                          <input
                            type="text"
                            value={date.location.postalCode}
                            onChange={(e) => handleLocationChange(index, 'postalCode', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>

                        <div className="sm:col-span-2">
                          <label className="block text-sm font-medium text-gray-700">
                            Informations complémentaires
                          </label>
                          <textarea
                            rows={2}
                            value={date.location.additionalInfo}
                            onChange={(e) => handleLocationChange(index, 'additionalInfo', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

            <button
              type="button"
              onClick={addDate}
              className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <PlusIcon className="-ml-1 mr-2 h-4 w-4" />
              Ajouter une date
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {isEditing ? (
              loading ? 'Enregistrement...' : 'Enregistrer les modifications'
            ) : (
              loading ? 'Création...' : 'Créer l\'événement'
            )}
          </button>
        </div>
          </form>
        </div>
      </div>

    </>
  );
}
